import React from "react";
import PropTypes from "prop-types";
import styles from "./MessageBubble.module.css";
import {
  getMediaPlaceholder,
  isMediaPlaceholderText,
} from "./messageCenterHelpers";

/**
 * Bolha individual da thread do Centro de Mensagens.
 *
 * Semântica (distinta da do Suporte):
 *  - `kind === "client"` → mensagem do cliente: alinhada à ESQUERDA, fundo
 *    branco, sem rótulo de origem.
 *  - `kind === "ai"` → resposta da IA: alinhada à DIREITA, fundo Nexus Blue,
 *    texto branco, rótulo "🤖 IA".
 *  - `kind === "human"` → resposta do atendente: alinhada à DIREITA, fundo azul
 *    escuro + faixa de acento clara, texto branco, rótulo "👤 Atendente".
 *    O rótulo é "Atendente" e não "Você" porque a mensagem pode ter saído do
 *    app do WhatsApp Business de OUTRA pessoa da equipe (coexistência), não
 *    necessariamente do usuário logado.
 *
 * `mediaType` (`image` / `audio` / `document` / outros) renderiza um placeholder
 * textual — "📷 Foto recebida", "🎧 Áudio recebido"… A `mediaUrl` da coexistência
 * é um `asset_id` da Meta, não uma URL pública: a mídia NÃO é renderizada.
 * Havendo legenda/transcrição em `text`, ela aparece junto do placeholder.
 * `pending` (envio otimista) reduz a opacidade até o backend confirmar.
 */
export default function MessageBubble({ text, kind, mediaType, pending }) {
  const isOutgoing = kind === "ai" || kind === "human";
  const rowClass = isOutgoing ? styles.outgoing : styles.incoming;

  const bubbleKindClass =
    kind === "client"
      ? styles.bubbleClient
      : kind === "ai"
        ? styles.bubbleAi
        : styles.bubbleHuman;

  const originLabel =
    kind === "ai" ? "🤖 IA" : kind === "human" ? "👤 Atendente" : null;

  const media = getMediaPlaceholder(mediaType);
  const caption = media && !isMediaPlaceholderText(text) ? text : "";

  return (
    <div className={`${styles.row} ${rowClass}`}>
      <div
        className={`${styles.bubble} ${bubbleKindClass} ${pending ? styles.pending : ""}`}
      >
        {media ? (
          <span className={styles.mediaChip}>
            <span aria-hidden="true">{media.icon}</span>
            <span>
              {media.full}
              {caption ? `: "${caption}"` : ""}
            </span>
          </span>
        ) : (
          <p className={styles.content}>{text}</p>
        )}
      </div>
      {(originLabel || pending) && (
        <span className={styles.meta}>
          {originLabel && <strong>{originLabel}</strong>}
          {pending && <span className={styles.sending}> · enviando…</span>}
        </span>
      )}
    </div>
  );
}

MessageBubble.propTypes = {
  text: PropTypes.string,
  kind: PropTypes.oneOf(["client", "ai", "human"]).isRequired,
  /** `image` | `audio` | `document` | qualquer outro tipo da Meta. */
  mediaType: PropTypes.string,
  pending: PropTypes.bool,
};
